import { useState } from "react";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatErr } from "../lib/api";

const cities = ["Mumbai", "Bengaluru", "Delhi NCR", "Pune", "Hyderabad", "Chennai", "Kolkata", "Ahmedabad", "Other"];
const budgets = ["Under ₹50L", "₹50L – ₹1Cr", "₹1Cr – ₹2.5Cr", "₹2.5Cr – ₹5Cr", "₹5Cr+"];
const propertyTypes = ["Apartment", "Villa / Independent House", "Plot / Land", "Commercial", "Investment property"];

export default function LeadForm() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        budget: "",
        property_type: "",
        message: "",
    });
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone || !form.city) {
            toast.error("Please fill in name, email, phone and city.");
            return;
        }
        setLoading(true);
        try {
            await api.post("/leads", { ...form, source: "website-hero" });
            setSubmitted(true);
            toast.success("Thank you. A partner will reach out within 24 hours.");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail) || "Something went wrong.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="lead" data-testid="lead-section" className="relative py-24 sm:py-32 px-6 sm:px-10 lg:px-16 border-t border-white/5">
            <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-16 items-start">
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="h-px w-10 bg-[#D4AF37]" />
                        <span className="overline">Get Started</span>
                    </div>
                    <h2 className="font-serif-display text-4xl sm:text-5xl tracking-tight leading-[1.05]">
                        Ready to Build a Predictable Flow <br />
                        of <span className="italic text-[#D4AF37]">Buyer Enquiries</span>?
                    </h2>
                    <p className="mt-6 text-white/70 leading-relaxed">
                        Share a few details. A senior lead strategist will reach out within one business day with a
                        tailored proposal for your market.
                    </p>
                    <div className="mt-10 space-y-4 text-sm text-white/60">
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                            <span>No monthly retainer, pay per verified lead</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                            <span>AI-assisted qualification before every delivery</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <CheckCircle2 size={16} className="text-[#D4AF37]" strokeWidth={1.5} />
                            <span>Handled with applicable RERA & DPDP requirements in mind</span>
                        </div>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    {!submitted ? (
                        <form
                            onSubmit={submit}
                            data-testid="lead-form"
                            className="bg-[#0A0B10] border border-[#D4AF37]/25 p-8 lg:p-10"
                        >
                            <div className="grid sm:grid-cols-2 gap-5">
                                <Field label="Full name" required>
                                    <input
                                        data-testid="lead-name"
                                        className="hol-input"
                                        placeholder="Rohan Mehta"
                                        value={form.name}
                                        onChange={(e) => set("name", e.target.value)}
                                    />
                                </Field>
                                <Field label="Work email" required>
                                    <input
                                        data-testid="lead-email"
                                        type="email"
                                        className="hol-input"
                                        placeholder="you@company.com"
                                        value={form.email}
                                        onChange={(e) => set("email", e.target.value)}
                                    />
                                </Field>
                                <Field label="Phone (WhatsApp)" required>
                                    <input
                                        data-testid="lead-phone"
                                        className="hol-input"
                                        placeholder="+91 98XXXXXXXX"
                                        value={form.phone}
                                        onChange={(e) => set("phone", e.target.value)}
                                    />
                                </Field>
                                <Field label="City / Micro-market" required>
                                    <select
                                        data-testid="lead-city"
                                        className="hol-input"
                                        value={form.city}
                                        onChange={(e) => set("city", e.target.value)}
                                    >
                                        <option value="">Select city</option>
                                        {cities.map((c) => (
                                            <option key={c} value={c}>
                                                {c}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Budget range">
                                    <select
                                        data-testid="lead-budget"
                                        className="hol-input"
                                        value={form.budget}
                                        onChange={(e) => set("budget", e.target.value)}
                                    >
                                        <option value="">Select budget</option>
                                        {budgets.map((b) => (
                                            <option key={b} value={b}>
                                                {b}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Property type">
                                    <select
                                        data-testid="lead-property-type"
                                        className="hol-input"
                                        value={form.property_type}
                                        onChange={(e) => set("property_type", e.target.value)}
                                    >
                                        <option value="">Select type</option>
                                        {propertyTypes.map((p) => (
                                            <option key={p} value={p}>
                                                {p}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>

                            <div className="mt-5">
                                <Field label="Anything specific? (optional)">
                                    <textarea
                                        data-testid="lead-message"
                                        rows={3}
                                        className="hol-input resize-none"
                                        placeholder="Tell us about your current lead sources, monthly volume, target closures..."
                                        value={form.message}
                                        onChange={(e) => set("message", e.target.value)}
                                    />
                                </Field>
                            </div>

                            <button
                                type="submit"
                                data-testid="lead-submit"
                                disabled={loading}
                                className="btn-gold mt-8 w-full justify-center disabled:opacity-60"
                            >
                                {loading ? "Submitting..." : (
                                    <>
                                        Start Your Campaign <ArrowRight size={16} />
                                    </>
                                )}
                            </button>

                            <p className="mt-4 text-[11px] text-white/40 text-center">
                                By submitting, you agree to Lead Krafts' privacy policy. We never spam and never
                                share your data.
                            </p>
                        </form>
                    ) : (
                        <div
                            data-testid="lead-success"
                            className="bg-[#0A0B10] border border-[#D4AF37]/40 p-12 text-center"
                        >
                            <div className="mx-auto h-16 w-16 grid place-items-center border border-[#D4AF37]">
                                <CheckCircle2 size={32} className="text-[#D4AF37]" strokeWidth={1.2} />
                            </div>
                            <h3 className="mt-6 font-serif-display text-3xl">Thank you, {form.name.split(" ")[0]}.</h3>
                            <p className="mt-4 text-white/70 max-w-md mx-auto">
                                A senior lead strategist will reach out on{" "}
                                <span className="text-[#D4AF37]">{form.phone}</span> within one business day.
                            </p>
                            <button
                                onClick={() => {
                                    setSubmitted(false);
                                    setForm({
                                        name: "",
                                        email: "",
                                        phone: "",
                                        city: "",
                                        budget: "",
                                        property_type: "",
                                        message: "",
                                    });
                                }}
                                className="btn-outline-gold mt-8"
                                data-testid="lead-reset"
                            >
                                Submit another enquiry
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-xs uppercase tracking-widest text-white/50 mb-2 block">
                {label}
                {required && <span className="text-[#D4AF37] ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}
